const Mt = (t) => Symbol(t);
function Ot(t, e) {
  return {
    type: t,
    render: e
  };
}
const b = [], ht = (t) => {
  b.push(t);
}, ft = () => {
  if (b.length === 0)
    throw new Error("Cannot pop from empty scope stack");
  b.pop();
}, Q = () => b[b.length - 1] ?? null, Pt = () => b, xt = () => b[b.length - 2] ?? null, Y = (t, e) => {
  ht(t);
  try {
    return e();
  } finally {
    ft();
  }
}, F = (t) => {
  const e = b.slice();
  b.length = 0;
  try {
    return t();
  } finally {
    b.length = 0, b.push(...e);
  }
}, f = (t, e) => t === e, et = /* @__PURE__ */ new WeakMap();
class v {
  /**
   * Creates a Signal that holds the result of a Promise, with proper error handling.
   *
   * This static method creates a signal that starts with an initial value and updates
   * when the promise resolves. If the promise rejects, an optional recovery function
   * can provide a fallback value.
   *
   * @example
   * ```typescript
   * // Basic usage with API call
   * const userData = Signal.ofPromise(
   *   fetch('/api/user').then(r => r.json()),
   *   { loading: true }, // initial state
   *   error => ({ error: error.message, loading: false }) // error recovery
   * )
   *
   * // Use in UI
   * Ensure(userData,
   *   (user) => html.div('Welcome, ', user.map(u => u.name)),
   *   () => html.div('Loading...')
   * )
   * ```
   *
   * @example
   * ```typescript
   * // With custom equality function
   * const config = Signal.ofPromise(
   *   loadConfig(),
   *   {},
   *   () => ({}),
   *   (a, b) => JSON.stringify(a) === JSON.stringify(b) // deep equality
   * )
   * ```
   *
   * @typeParam O - The type of the value returned by the Promise
   * @param promise - The Promise to use to feed the Signal
   * @param init - The initial value of the Signal before the Promise resolves
   * @param recover - Optional function to recover from Promise rejection and provide an alternative value
   * @param equals - Function to compare two values for equality (defaults to strict equality)
   * @returns A Signal that represents the result of the Promise
   */
  static ofPromise = (e, s, n, i = f) => {
    const r = new v(s, i);
    return e.then((o) => r._setAndNotify(o)).catch((o) => {
      n != null ? r._setAndNotify(n(o)) : console.error(
        "Unhandled promise rejection in Signal.ofPromise:",
        o
      );
    }), r;
  };
  /**
   * Checks if a value is a Signal.
   *
   * @param value - The value to check.
   * @returns `true` if the value is a Signal, `false` otherwise.
   */
  static is = (e) => (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    e != null && e.$__signal__ === !0
  );
  /**
   * @internal
   */
  _value;
  /**
   * @internal
   */
  _derivatives = null;
  /**
   * @internal
   */
  _onValueListeners = null;
  /**
   * @internal
   */
  _onDisposeListeners = null;
  /**
   * Represents a signal with a value of type T.
   *
   * @param value - The initial value of the signal.
   * @param equals - A function that determines whether two values of type T are equal.
   * @public
   */
  constructor(e, s) {
    this._value = e, s !== void 0 && s !== f && (this.equals = s);
  }
  /**
   * Gets the current value of the signal.
   * @returns The current value of the signal.
   */
  get() {
    return this._value;
  }
  /**
   * Gets the value of the signal.
   * @returns The current value of the signal.
   */
  get value() {
    return this._value;
  }
  /**
   * Checks if the signal has any registered listeners.
   * @returns `true` if the signal has listeners, `false` otherwise.
   */
  hasListeners() {
    return this._onValueListeners !== null && this._onValueListeners.length > 0 || this._derivatives !== null && this._derivatives.length > 0;
  }
  /**
   * Registers a listener function to be called whenever the value of the signal changes.
   * The listener function will be immediately called with the current value of the signal.
   * Returns a function that can be called to unregister the listener.
   *
   * When called within a DisposalScope (e.g., inside a renderable), the listener is
   * automatically cleaned up when the scope is disposed. This prevents memory leaks
   * when listening to outer-scope signals from inner scopes.
   *
   * @param listener - The listener function to be called when the value of the signal changes.
   * @param options - Options for the listener.
   *
   * @example
   * ```typescript
   * // Automatic cleanup when scope disposes
   * const MyComponent = () => {
   *   const outerSignal = prop(0)
   *
   *   return html.div(
   *     When(someCondition, () => {
   *       // This listener is automatically cleaned up when the When() disposes
   *       outerSignal.on(value => console.log(value))
   *       return html.span('Inner content')
   *     })
   *   )
   * }
   * ```
   */
  on(e, s = {}) {
    s.skipInitial || e(this.get(), void 0);
    const n = s.once ? (r, o) => {
      i(), e(r, o);
    } : e;
    this._onValueListeners === null && (this._onValueListeners = []), this._onValueListeners.push(n);
    const i = () => {
      if (this._onValueListeners === null) return;
      const r = this._onValueListeners.indexOf(n);
      r !== -1 && this._onValueListeners.splice(r, 1), s.abortSignal != null && s.abortSignal.removeEventListener("abort", i);
    };
    if (s.abortSignal != null && s.abortSignal.addEventListener("abort", i), !s.noAutoDispose) {
      const r = Q();
      r?.onDispose(i);
    }
    return i;
  }
  /**
   * Registers a listener function to be called whenever the value of the signal changes.
   * The listener function will not be called with the current value of the signal.
   * Returns a function that can be called to unregister the listener.
   *
   * @param listener - The listener function to be called when the value of the signal changes.
   * @param options - Options for the listener.
   */
  onChange(e, s = {}) {
    return s.skipInitial = !0, this.on(
      e,
      s
    );
  }
  /**
   * @internal
   */
  _setAndNotify(e) {
    if (this._disposed) return;
    const s = this._value;
    if (!this.equals(s, e)) {
      this._value = e;
      const i = this._derivatives;
      if (i !== null)
        for (let o = 0; o < i.length; o++) i[o].setDirty();
      const r = this._onValueListeners;
      if (r !== null)
        for (let o = 0; o < r.length; o++)
          r[o](e, s);
    }
  }
  /**
   * @internal
   */
  _disposed = !1;
  /**
   * Checks whether the signal is disposed.
   * @returns True if the signal is disposed, false otherwise.
   */
  isDisposed() {
    return this._disposed;
  }
  /**
   * Adds a listener function to be called when the object is disposed.
   * @param listener - The listener function to be called when the object is disposed.
   * @returns A function that can be called to remove the listener.
   */
  onDispose(e) {
    this._onDisposeListeners === null && (this._onDisposeListeners = []), this._onDisposeListeners.push(e);
  }
  /**
   * Disposes the signal, releasing any resources associated with it.
   * This clears all listeners, derivatives, and disposal callbacks.
   */
  dispose() {
    if (this._disposed) return;
    this._disposed = !0;
    const e = this._derivatives;
    this._derivatives = null;
    const s = this._onDisposeListeners;
    if (this._onDisposeListeners = null, this._onValueListeners = null, e !== null)
      for (let n = 0; n < e.length; n++) e[n].dispose();
    if (s !== null)
      for (let n = 0; n < s.length; n++) s[n]();
  }
  /**
   * Removes a computed from this signal's derivatives list.
   * Called by Computed.dispose() to clean up the parent→child link.
   * @internal
   */
  _removeDerivative(e) {
    if (this._derivatives !== null) {
      const s = this._derivatives.indexOf(e);
      s !== -1 && this._derivatives.splice(s, 1);
    }
  }
  /**
   * Creates a new computed signal by applying a transformation function to this signal's value.
   *
   * The `map` method is one of the most commonly used signal operations. It creates a new
   * computed signal that automatically updates whenever the source signal changes. The
   * transformation function is called with the current value and should return the new value.
   *
   * @example
   * ```typescript
   * const count = prop(5)
   *
   * // Transform to different types
   * const doubled = count.map(n => n * 2)
   * const message = count.map(n => `Count is ${n}`)
   * const isEven = count.map(n => n % 2 === 0)
   *
   * // Use in UI
   * html.div(
   *   html.div('Original: ', count.map(String)),
   *   html.div('Doubled: ', doubled.map(String)),
   *   html.div('Message: ', message),
   *   html.div('Is even: ', isEven.map(String))
   * )
   * ```
   *
   * @example
   * ```typescript
   * // Chain multiple transformations
   * const user = prop({ name: 'John', age: 30 })
   * const greeting = user
   *   .map(u => u.name)
   *   .map(name => name.toUpperCase())
   *   .map(name => `Hello, ${name}!`)
   * ```
   *
   * @example
   * ```typescript
   * // With custom equality function for objects
   * const items = prop([{ id: 1, name: 'Item 1' }])
   * const itemNames = items.map(
   *   items => items.map(item => item.name),
   *   (a, b) => JSON.stringify(a) === JSON.stringify(b) // deep equality
   * )
   * ```
   *
   * **Auto-Disposal:** The returned computed signal is automatically registered with the current
   * disposal scope (if one exists). When used within a renderable or `WithScope()`, the signal
   * will be automatically disposed when the component unmounts. No manual `OnDispose()` needed!
   *
   * ```typescript
   * const MyComponent: Renderable = (ctx) => {
   *   const count = prop(0);
   *   const doubled = count.map(x => x * 2);  // ✅ Auto-disposed
   *   return html.div(doubled);
   * };
   * ```
   *
   * @typeParam O - The type of the transformed value
   * @param fn - Function that transforms the signal's value to a new value
   * @param equals - Optional function to determine if two transformed values are equal (defaults to strict equality)
   * @returns A new computed signal with the transformed value (auto-registered with current scope)
   */
  map(e, s = f) {
    const n = new V(() => {
      try {
        return e(this.get());
      } catch (i) {
        throw console.error("Error in Signal.map:", i), i;
      }
    }, s);
    return this.setDerivative(n), n;
  }
  /**
   * Returns a new Signal that applies the given function to the value of the current Signal,
   * and then flattens the resulting Signal.
   *
   * @typeParam O - The type of the value emitted by the resulting Signal.
   * @param fn - The function to apply to the value of the current Signal.
   * @param equals - A function that determines whether two values of type O are equal.
   *               Defaults to a strict equality check (===).
   * @returns A new Signal that emits the values of the resulting Signal.
   */
  flatMap(e, s = f) {
    const n = new V(() => {
      try {
        return e(this.get()).get();
      } catch (i) {
        throw console.error("Error in Signal.flatMap:", i), i;
      }
    }, s);
    return this.setDerivative(n), n;
  }
  /**
   * Invokes a callback function with the current value of the signal, without modifying the signal.
   *
   * @param fn - The callback function to be invoked with the current value of the signal.
   * @returns A new signal that emits the same value as the original signal and invokes the callback function.
   */
  tap(e) {
    const s = this.map((n) => (e(n), n));
    return s.scheduleIfDirty(), s;
  }
  /**
   * Returns a new Signal that emits the value at the specified key of the current value.
   *
   * @param key - The key of the value to retrieve.
   * @returns A new Signal that emits the value at the specified key.
   */
  at(e) {
    return this.map((s) => s[e]);
  }
  /**
   * Represents a collection of signals mapping to each key/field in the wrapped value.
   * @typeParam T - The type of the signals.
   */
  get $() {
    let e = et.get(this);
    return e !== void 0 || (e = new Proxy(this, {
      get: (s, n) => this.at(n)
    }), et.set(this, e)), e;
  }
  filter(e, s) {
    let n = s ?? this.get();
    const i = new V(() => {
      try {
        const r = this.get();
        return n = e(r) ? r : n;
      } catch (r) {
        throw console.error("Error in Signal.filter:", r), r;
      }
    }, this.equals);
    return this.setDerivative(i), i;
  }
  /**
   * Returns a new Computed object that applies the provided mapping function to the value of this Signal,
   * and filters out values that are `undefined` or `null`.
   *
   * @typeParam O - The type of the mapped value.
   * @param fn - The mapping function to apply to the value of this Signal.
   * @param startValue - The initial value for the Computed object.
   * @param equals - Optional equality function to determine if two values are equal.
   * @returns - A new Computed object with the mapped and filtered values.
   */
  filterMap(e, s, n = f) {
    let i = s;
    const r = new V(() => {
      try {
        const o = this.get(), l = e(o);
        return i = l ?? i;
      } catch (o) {
        throw console.error("Error in Signal.filterMap:", o), o;
      }
    }, n);
    return this.setDerivative(r), r;
  }
  /**
   * Maps the values emitted by the signal to a new value asynchronously using the provided function.
   * If the function throws an error, it will be caught and logged.
   * If a recovery function is provided, it will be called with the error and its return value will be used as the mapped value.
   * If no recovery function is provided, the error will be logged as an unhandled promise rejection.
   *
   * @typeParam O - The type of the mapped value.
   * @param fn - The function to map the values emitted by the signal. The second argument to this function allows to cancel the previously running mapping function if it has not completed by the time a new value is emitted.
   * @param alt - The alternate value to use if the signal is disposed or the mapping function throws an error.
   * @param recover - The recovery function to handle errors thrown by the mapping function.
   * @param equals - The equality function to compare the mapped values for equality.
   * @returns A property that holds the mapped value and can be observed for changes.
   */
  mapAsync(e, s, n, i = f) {
    const r = C(s, i);
    let o = 0, l = new AbortController();
    return r.onDispose(
      this.on(async (a) => {
        const c = ++o;
        l.abort(), l = new AbortController();
        try {
          const h = await e(a, { abortSignal: l.signal });
          c === o && r.set(h);
        } catch (h) {
          if (c === o)
            if (n != null)
              r.set(n(h));
            else
              throw h;
        }
      })
    ), r;
  }
  /**
   * Maps the values emitted by the signal to multiple values over time using an async generator.
   * Each time the source signal changes, a new async generator is created and iterated.
   * Previous generators are aborted when a new value arrives.
   *
   * This is useful for streaming data, such as:
   * - AI/LLM streaming responses
   * - Server-Sent Events (SSE)
   * - Paginated data loading
   * - WebSocket message streams
   *
   * @example
   * ```typescript
   * const query = prop('hello')
   *
   * // Stream AI response tokens
   * const streamingResponse = query.mapAsyncGenerator(
   *   async function* (q, { abortSignal }) {
   *     const response = await fetch('/api/stream?q=' + q, { signal: abortSignal })
   *     const reader = response.body!.getReader()
   *     let accumulated = ''
   *
   *     while (true) {
   *       const { done, value } = await reader.read()
   *       if (done) break
   *       accumulated += new TextDecoder().decode(value)
   *       yield accumulated  // Update signal with each chunk
   *     }
   *   },
   *   '',  // alt: empty string while loading
   *   (err) => 'Error: ' + err  // recover
   * )
   * ```
   *
   * @typeParam O - The type of the yielded values.
   * @param fn - The async generator function that yields values over time. The second argument provides an AbortSignal to cancel the generator when a new value arrives.
   * @param alt - The initial value to use before the first yield.
   * @param recover - Optional function to handle errors thrown by the generator.
   * @param equals - Optional equality function to compare yielded values.
   * @returns A property that updates each time the generator yields a value.
   */
  mapAsyncGenerator(e, s, n, i = f) {
    const r = C(s, i);
    let o = 0, l = new AbortController();
    return r.onDispose(
      this.on(async (a) => {
        const c = ++o;
        l.abort(), l = new AbortController();
        try {
          const h = e(a, { abortSignal: l.signal });
          for await (const u of h) {
            if (c !== o) return;
            r.set(u);
          }
        } catch (h) {
          if (c === o)
            if (n != null)
              r.set(n(h));
            else
              throw h;
        }
      })
    ), r;
  }
  /**
   * Maps the values of the signal using the provided function `fn`, and returns a new signal
   * containing the mapped values. If the mapped value is `undefined` or `null`, it is replaced
   * with the provided `alt` value.
   *
   * @typeParam O - The type of the mapped value.
   * @param fn - The function used to map the values of the signal.
   * @param alt - The alternative value to use when the mapped value is `undefined` or `null`.
   * @returns A new signal containing the mapped values.
   */
  mapMaybe(e, s) {
    return this.map((n) => e(n) ?? s);
  }
  /**
   * Feeds a property into the signal and sets up disposal behavior.
   * @param prop - The property to feed into the signal.
   * @param autoDisposeProp - Determines whether the property should be automatically disposed when the signal is disposed.
   * @returns The input property.
   */
  feedProp(e, s = !1) {
    const n = this.on((i) => e.set(i), {
      noAutoDispose: !s
    });
    return e.onDispose(n), s ? this.onDispose(() => e.dispose()) : this.onDispose(n), e;
  }
  /**
   * Derives a new property from the current signal.
   * @param options - The options for the derived property.
   * @param options.autoDisposeProp - Determines whether the derived property should be automatically disposed.
   * @param options.equals - A function that determines if two values are equal.
   * @returns The derived property.
   */
  deriveProp({
    autoDisposeProp: e = !0,
    equals: s
  } = {}) {
    return this.feedProp(C(this.get(), s), e);
  }
  /**
   * Derives a new signal from the current signal. Useful to create a new signal that emits the same values as the current signal but can be disposed independently.
   * @returns A new signal that emits the same values as the current signal.
   */
  derive() {
    return this.map((e) => e);
  }
  /**
   * Returns a signal that emits the count of values received so far.
   * @returns A signal that emits the count of values received so far.
   */
  count() {
    let e = 0;
    return this.map(() => ++e);
  }
  /**
   * Adds a computed value as a derivative of the signal.
   * Uses structural parent↔child references instead of closure-based disposal wiring.
   * Parent.dispose() directly disposes derivatives; Computed.dispose() removes itself from parents.
   * @param computed - The computed value to add as a derivative.
   */
  setDerivative(e) {
    this._derivatives === null && (this._derivatives = []), this._derivatives.push(e), e._addParent(this);
  }
}
v.prototype.$__signal__ = !0;
v.prototype.equals = f;
const dt = typeof queueMicrotask == "function" ? queueMicrotask : (t) => Promise.resolve().then(t);
let j = null;
const pt = (t) => {
  j === null ? (j = [t], dt(() => {
    const e = j;
    j = null;
    for (let s = 0; s < e.length; s++)
      try {
        e[s]();
      } catch (n) {
        console.error(n);
      }
  })) : j.push(t);
};
class V extends v {
  /**
   * Creates a new Computed signal.
   *
   * **Auto-Registration:** This constructor automatically registers the signal with the current
   * disposal scope (if one exists). This ensures that computed signals created by methods like
   * `.map()`, `.flatMap()`, `.filter()`, etc. are automatically tracked and disposed.
   *
   * When a computed signal is created within a renderable or `WithScope()`, it will be
   * automatically disposed when the component unmounts or the scope is disposed.
   *
   * To create a computed signal that outlives the current scope, use `untracked()`:
   * ```typescript
   * const globalSignal = untracked(() => mySignal.map(x => x * 2));
   * // Remember to dispose manually: globalSignal.dispose()
   * ```
   *
   * @param _fn - The function that computes the value of the signal.
   * @param equals - The function used to compare two values of type T for equality.
   *
   * @see {@link computed} - Factory function for creating computed signals with explicit dependencies
   * @see {@link Signal.map} - Creates a computed signal by transforming values
   * @see {@link getCurrentScope} - Get the current disposal scope
   * @see {@link untracked} - Create signals outside of scope tracking
   */
  constructor(e, s) {
    super(void 0, s), this._fn = e;
    const n = Q();
    n?.track(this);
  }
  /**
   * Checks if a value is an instance of `Computed`.
   *
   * @param value - The value to check.
   * @returns `true` if the value is an instance of `Computed`, `false` otherwise.
   */
  static is(e) {
    return e != null && e.$__computed__ === !0;
  }
  /**
   * @internal
   */
  _isDirty = !0;
  /**
   * @internal — parent signals that have this as a derivative.
   * Used for structural disposal: Computed.dispose() removes itself from parents.
   */
  _parents = null;
  /**
   * Registers a parent signal that has this computed as a derivative.
   * @internal
   */
  _addParent(e) {
    this._parents === null ? this._parents = [e] : this._parents.push(e);
  }
  /**
   * Marks the signal as dirty, indicating that its value has changed and needs to be recalculated.
   * If the signal is already dirty or disposed, this method does nothing.
   * It also marks all dependent signals as dirty and schedules a notification to update their values.
   */
  setDirty() {
    if (this._isDirty || this._disposed) return;
    this._isDirty = !0;
    const e = this._derivatives;
    if (e !== null)
      for (let s = 0; s < e.length; s++) e[s].setDirty();
    this._scheduleNotify();
  }
  /**
   * @internal
   */
  _scheduleCount = 0;
  /**
   * Schedules a notification to be executed asynchronously.
   * If the signal is dirty, it will be updated and notified.
   * @internal
   */
  _scheduleNotify() {
    const e = ++this._scheduleCount;
    pt(() => {
      this._scheduleCount !== e || this._disposed || this._isDirty && (this._isDirty = !1, this._setAndNotify(this._fn()));
    });
  }
  /**
   * Schedule a microtask to compute this signal if it is dirty.
   * Used by computed() factory to ensure effects fire their initial computation.
   * @internal
   */
  scheduleIfDirty() {
    this._isDirty && this._scheduleNotify();
  }
  /** {@inheritDoc Signal.get} */
  get() {
    return this._isDirty && !this._disposed && (this._isDirty = !1, this._setAndNotify(this._fn())), this._value;
  }
  /** {@inheritDoc Signal.value} */
  get value() {
    return this.get();
  }
  /**
   * Disposes the computed signal and cancels any pending recomputations.
   * Uses structural parent references to remove itself from parents' derivative lists,
   * then directly disposes own derivatives.
   */
  dispose() {
    if (this._disposed) return;
    this._scheduleCount++, this._disposed = !0;
    const e = this._parents;
    if (this._parents = null, e !== null)
      for (let i = 0; i < e.length; i++)
        e[i]._removeDerivative(this);
    const s = this._derivatives;
    this._derivatives = null;
    const n = this._onDisposeListeners;
    if (this._onDisposeListeners = null, this._onValueListeners = null, this._fn = null, s !== null)
      for (let i = 0; i < s.length; i++) s[i].dispose();
    if (n !== null)
      for (let i = 0; i < n.length; i++) n[i]();
  }
}
V.prototype.$__computed__ = !0;
class L extends v {
  /**
   * Checks if a value is a Prop.
   * @param value - The value to check.
   * @returns `true` if the value is a Prop, `false` otherwise.
   */
  static is = (e) => (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    e != null && e.$__prop__ === !0
  );
  /**
   * Changes the value of the property and notifies its listeners.
   *
   * @param value - The new value of the property.
   */
  set(e) {
    this._setAndNotify(e);
  }
  /**
   * Updates the value of the signal by applying the provided function to the current value.
   * @param fn - The function to apply to the current value.
   */
  update(e) {
    this._setAndNotify(e(this.get()));
  }
  /**
   * Creates a reducer function that combines the provided reducer function and effects.
   * @param fn - The reducer function that takes the current state and an action, and returns the new state.
   * @param effects - An array of effects to be executed after the state is updated.
   * @returns A dispatch function that can be used to update the state and trigger the effects.
   */
  reducer(e, ...s) {
    const n = this;
    return function i(r) {
      const o = n.value;
      n.update((l) => e(l, r)), !n.equals(o, n.value) && s.forEach(
        (l) => l({
          previousState: o,
          state: n.value,
          action: r,
          dispatch: i
        })
      );
    };
  }
  /**
   * Creates an isomorphism for the Signal.
   * An isomorphism is a pair of functions that convert values between two types,
   * along with an equality function to compare values of the second type.
   *
   * @param to - A function that converts values from type T to type O.
   * @param from - A function that converts values from type O to type T.
   * @param equals - An optional function that compares values of type O for equality.
   *                Defaults to a strict equality check (===).
   * @returns A Prop object representing the isomorphism.
   */
  iso(e, s, n = f) {
    const i = new L(e(this.get()), n);
    return i.onDispose(this.on((r) => i.set(e(r)))), i.on((r) => this._setAndNotify(s(r))), i;
  }
  /**
   * Returns a `Prop` that represents the value at the specified key of the current value.
   *
   * @param key - The key of the value to access.
   * @returns A `Prop` that represents the value at the specified key.
   */
  atProp(e) {
    return this.iso(
      (s) => s[e],
      (s) => ({ ...this.value, [e]: s })
    );
  }
  /**
   *  Access for the current value of the property.
   */
  get value() {
    return this.get();
  }
  set value(e) {
    this._setAndNotify(e);
  }
}
L.prototype.$__prop__ = !0;
const U = (t, e, s = f) => {
  const n = new V(t, s);
  return e.forEach((i) => i.setDerivative(n)), n.scheduleIfDirty(), n;
}, rt = (t, e, s = {}) => {
  let n = s.once ? () => {
    h(), t();
  } : t;
  if (s.skipInitial) {
    let u = !1;
    const p = n;
    n = () => {
      u ? p() : u = !0;
    };
  }
  const i = n, r = e.length, o = new Array(r);
  let l = !1;
  const c = U(() => {
    let u = !l;
    for (let p = 0; p < r; p++) {
      const g = e[p].get();
      g !== o[p] && (o[p] = g, u = !0);
    }
    l = !0, u && i();
  }, e), h = () => {
    c.dispose(), s.abortSignal != null && s.abortSignal.removeEventListener("abort", h);
  };
  return s.abortSignal != null && s.abortSignal.addEventListener("abort", h), h;
}, C = (t, e = f) => {
  const s = new L(t, e), n = Q();
  return n?.track(s), s;
}, gt = (t, e = f) => new v(t, e);
function ot(t) {
  return t != null && t !== !1 && t !== 0 && t !== "";
}
function _t(t) {
  return !ot(t);
}
function mt(t) {
  return t == null;
}
function vt(t) {
  return t != null;
}
const S = {
  /**
   * Maps a value or a Signal to a new value.
   * If the value is a Signal, it returns a new Signal with the mapped value.
   * If the value is not a Signal, it returns the mapped value.
   *
   * @typeParam T - The type of the value.
   * @typeParam U - The type of the new value.
   * @param value - The value or Signal to map.
   * @param fn - The function to map the value.
   * @returns The mapped value.
   */
  map: (t, e) => v.is(t) ? t.map(e) : e(t),
  /**
   * Wraps a value or a Signal instance into a Signal.
   * If the value is already a Signal, it returns the value itself.
   * If the value is not a Signal, it creates a new Signal instance with the given value.
   *
   * @typeParam O - The type of the value.
   * @param value - The value or Signal instance to wrap.
   * @param equals - A function that determines if two values are equal. Defaults to strict equality (===).
   * @returns A Signal instance.
   */
  toSignal: (t, e) => v.is(t) ? t.derive() : gt(t, e),
  /**
   * Wraps a value in a `Signal` if it is not already a `Signal`.
   * If the value is `null` or `undefined`, it returns `null` or `undefined` respectively.
   * @param value - The value to wrap or check.
   * @returns The wrapped value if it is not `null` or `undefined`, otherwise `null` or `undefined`.
   */
  maybeToSignal: (t, e) => {
    if (t != null)
      return S.toSignal(t, e);
  },
  /**
   * Gets the value from a `Signal` or the value itself if it is not a `Signal`.
   * @param value - The value or Signal instance to get the value from.
   * @returns The value.
   */
  get: (t) => v.is(t) ? t.get() : t,
  /**
   * Adds a listener to a `Signal` or calls the listener immediately if it is not a `Signal`.
   * @param value - The value or Signal instance to add the listener to.
   * @param listener - The listener to call when the value changes.
   * @returns A function to remove the listener.
   */
  on: (t, e) => v.is(t) ? t.on(e) : (e(t), () => {
  }),
  /**
   * Disposes of a value or a Signal.
   * If the value is a Signal, it disposes of the Signal.
   * If the value is not a Signal, it does nothing.
   * @param value - The value or Signal instance to dispose of.
   */
  dispose: (t) => {
    v.is(t) && t.dispose();
  },
  /**
   * Returns a function that disposes of a value or a Signal.
   * If the value is a Signal, it returns a function that disposes of the Signal.
   * If the value is not a Signal, it returns a function that does nothing.
   * @param value - The value or Signal instance to dispose of.
   * @returns A function to dispose of the value or Signal.
   */
  disposeFn: (t) => () => S.dispose(t),
  /**
   * Derives a Prop from a Signal.
   * If the value is a Signal, it returns a new Prop with the derived value.
   * If the value is not a Signal, it returns a new Prop with the value.
   * @param value - The value or Signal instance to derive the Prop from.
   * @param options - The options for the derived Prop.
   * @param options.autoDisposeProp - Determines whether the derived Prop should be automatically disposed.
   * @param options.equals - A function that determines if two values are equal.
   * @returns A Prop instance.
   */
  deriveProp: (t, {
    autoDisposeProp: e = !0,
    equals: s
  } = {}) => v.is(t) ? t.deriveProp({ autoDisposeProp: e, equals: s }) : C(t, s),
  /**
   * Creates a new signal that emits `true` if the value is truthy, `false` otherwise.
   * @param value - The value or signal to check.
   * @returns A signal that emits `true` if the value is truthy, `false` otherwise.
   */
  truthy: (t) => S.map(t, ot),
  /**
   * Creates a new signal that emits `true` if the value is falsy, `false` otherwise.
   * @param value - The value or signal to check.
   * @returns A signal that emits `true` if the value is falsy, `false` otherwise.
   */
  falsy: (t) => S.map(t, _t),
  /**
   * Creates a new signal that emits `true` if the value is null or undefined, `false` otherwise.
   * @param value - The value or signal to check.
   * @returns A signal that emits `true` if the value is null or undefined, `false` otherwise.
   */
  nil: (t) => S.map(t, mt),
  /**
   * Creates a new signal that emits `true` if the value is not null or undefined, `false` otherwise.
   * @param value - The value or signal to check.
   * @returns A signal that emits `true` if the value is not null or undefined, `false` otherwise.
   */
  defined: (t) => S.map(t, vt)
}, x = (...t) => (e, s) => {
  if (t.length === 1)
    return S.toSignal(t[0]).map(e);
  const n = t.filter((i) => v.is(i));
  return U(
    () => e(...t.map((i) => S.get(i))),
    n,
    s
  );
}, Nt = (...t) => (e, s, n, i = f) => x(...t)((...r) => r).mapAsync(
  ([...r], o) => e(
    ...r,
    o
  ),
  s,
  n,
  i
), Ft = (...t) => (e, s, n, i = f) => x(...t)((...r) => r).mapAsyncGenerator(
  ([...r], o) => e(
    ...r,
    o
  ),
  s,
  n,
  i
), Vt = (t) => {
  const e = Object.keys(t);
  return x(...Object.values(t))(
    (...s) => Object.fromEntries(e.map((n, i) => [n, s[i]]))
  );
}, yt = (...t) => (e, s = {}) => {
  const n = t.filter((i) => v.is(i));
  return rt(
    () => e(...t.map(S.get)),
    n,
    s
  );
};
class St {
  _signals = null;
  _callbacks = null;
  _disposed = !1;
  /**
   * Register a signal with this scope for automatic disposal.
   *
   * @param signal - The signal to track
   * @public
   */
  track(e) {
    this._signals === null && (this._signals = []), this._signals.push(e);
  }
  /**
   * Register a disposal callback to be called when this scope is disposed.
   * Callbacks are called before signals are disposed.
   * Use this for cleanup that doesn't need the `removeTree` parameter.
   *
   * @param callback - The callback to call on disposal
   * @public
   */
  onDispose(e) {
    this._callbacks === null && (this._callbacks = []), this._callbacks.push(e);
  }
  /**
   * Dispose all signals tracked by this scope.
   * This method is idempotent - calling it multiple times is safe.
   *
   * @public
   */
  dispose() {
    if (this._disposed) return;
    this._disposed = !0;
    const e = this._callbacks;
    if (this._callbacks = null, e !== null)
      for (let n = 0; n < e.length; n++)
        try {
          e[n]();
        } catch (i) {
          const r = i instanceof Error ? i.message : String(i);
          console.error("Error in disposal callback:", r);
        }
    const s = this._signals;
    if (this._signals = null, s !== null)
      for (let n = 0; n < s.length; n++)
        s[n].dispose();
  }
  /**
   * Check if this scope has been disposed.
   *
   * @returns true if the scope has been disposed
   * @public
   */
  get disposed() {
    return this._disposed;
  }
  /**
   * Returns the signals tracked by this scope.
   * Used by dev tools (HMR, signal inspector) to introspect scope contents.
   *
   * @returns Read-only array of tracked signals, empty if none or after disposal
   * @public
   */
  getTrackedSignals() {
    return this._signals ?? [];
  }
  /**
   * Creates a prop signal and tracks it in this scope.
   * Use this method in async contexts where automatic tracking doesn't work.
   *
   * @param value - The initial value
   * @param equals - Optional equality function
   * @returns A tracked Prop signal
   * @public
   */
  prop(e, s) {
    const n = F(() => C(e, s));
    return this.track(n), n;
  }
  /**
   * Creates a computed signal and tracks it in this scope.
   * Use this method in async contexts where automatic tracking doesn't work.
   *
   * @param fn - The computation function
   * @param dependencies - Array of signals this computed depends on
   * @param equals - Optional equality function
   * @returns A tracked Computed signal
   * @public
   */
  computed(e, s, n) {
    const i = F(() => U(e, s, n));
    return this.track(i), i;
  }
  /**
   * Creates an effect and tracks it in this scope.
   * Use this method in async contexts where automatic tracking doesn't work.
   *
   * @param fn - The effect function
   * @param signals - Array of signals to listen to
   * @param options - Optional listener options
   * @returns A clear function (the effect itself is tracked in the scope)
   * @public
   */
  effect(e, s, n) {
    return Y(this, () => rt(e, s, n));
  }
  /**
   * Creates a computed signal with curried signature and tracks it in this scope.
   * Use this method in async contexts where automatic tracking doesn't work.
   *
   * @param args - Values or signals to compute from
   * @returns A function that takes the computation function and returns a tracked Computed signal
   * @public
   */
  computedOf(...e) {
    return (s, n) => {
      const i = F(() => x(...e)(s, n));
      return this.track(i), i;
    };
  }
  /**
   * Creates an effect with curried signature and tracks it in this scope.
   * Use this method in async contexts where automatic tracking doesn't work.
   *
   * @param args - Values or signals to listen to
   * @returns A function that takes the effect function and returns a clear function
   * @public
   */
  effectOf(...e) {
    return (s, n) => Y(this, () => yt(...e)(s, n));
  }
}
const Bt = (t) => {
  const e = new St();
  try {
    return Y(e, () => t(e));
  } finally {
    e.dispose();
  }
}, G = () => (
  /* c8 ignore next */
  typeof window < "u" ? window : void 0
), wt = (t, e, s) => t + (e - t) * s, st = 97, Dt = (t, e, s) => {
  const n = Math.max(t.length, e.length);
  let i = "";
  for (let r = 0; r < n; r++) {
    let o = t.charCodeAt(r);
    isNaN(o) && (o = st);
    let l = e.charCodeAt(r);
    isNaN(l) && (l = st), i += String.fromCharCode(o + (l - o) * s);
  }
  return i;
}, It = (t, e, s) => new Date(t.getTime() + (e.getTime() - t.getTime()) * s), bt = (t, e) => e, Ct = (t) => typeof t == "number" ? wt : typeof t == "string" ? Dt : t instanceof Date ? It : bt;
class lt {
  _store = /* @__PURE__ */ new Map();
  /**
   * Retrieves the value associated with the specified key from the memory store.
   * @param key - The key to retrieve the value for.
   * @returns The value associated with the key, or `null` if the key is not found.
   */
  getItem = (e) => this._store.get(e) ?? null;
  /**
   * Sets the value associated with the specified key in the memory store.
   * @param key - The key to set the value for.
   * @param value - The value to set.
   */
  setItem = (e, s) => {
    this._store.set(e, s);
  };
}
let H = null, X = null;
const Et = () => (H || (H = new lt()), H), Lt = () => (X || (X = new lt()), X), at = ({
  key: t,
  defaultValue: e,
  store: s,
  serialize: n = JSON.stringify,
  deserialize: i = JSON.parse,
  equals: r = f,
  onLoad: o = (c) => c,
  syncTabs: l = !0,
  onKeyChange: a = "load"
}) => {
  let c = S.get(t);
  const h = s.getItem(c), u = new L(
    h != null ? o(i(h)) : typeof e == "function" ? e() : e,
    r
  ), p = G(), g = l && typeof p?.BroadcastChannel == "function";
  let d = !1, w = null, I = null;
  const y = (m) => {
    if (!g) return null;
    const M = `tempo:storedProp:${m}`, E = new p.BroadcastChannel(M), O = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`, k = (B) => {
      const D = B.data;
      if (!(D == null || typeof D != "object" || D.key !== m || typeof D.value != "string" || D.sourceId != null && D.sourceId === O))
        try {
          d = !0;
          const $ = o(i(D.value));
          u.set($);
        } catch ($) {
          console.warn(
            `Failed to sync storedProp for key "${m}" via BroadcastChannel`,
            $
          );
        } finally {
          d = !1;
        }
    };
    return E.addEventListener("message", k), u.onDispose(() => {
      E?.removeEventListener("message", k), E?.close();
    }), { channel: E, instanceId: O, handleMessage: k };
  }, A = y(c);
  A && (w = A.channel, I = A.instanceId);
  const N = (m) => {
    const M = c;
    if (M === m) return;
    const E = u.get(), O = n(E);
    if (s.setItem(M, O), w != null && (w.close(), w = null, I = null), c = m, a === "load") {
      const B = s.getItem(m);
      if (B != null)
        try {
          const D = o(i(B));
          u.set(D);
        } catch (D) {
          console.warn(
            `Failed to load storedProp from new key "${m}"`,
            D
          );
        }
      else
        s.setItem(m, O);
    } else a === "migrate" && s.setItem(m, O);
    const k = y(m);
    k && (w = k.channel, I = k.instanceId);
  };
  return v.is(t) && u.onDispose(t.on(N)), u.on((m, M) => {
    const E = n(m);
    s.setItem(c, E), w != null && !d && M !== void 0 && I != null && w.postMessage({
      key: c,
      value: E,
      sourceId: I
    });
  }), u;
}, $t = (t) => {
  const s = G()?.localStorage, n = s && typeof s.getItem == "function" ? s : Et();
  return at({
    ...t,
    store: n
  });
}, Tt = (t) => {
  const s = G()?.sessionStorage, n = s && typeof s.getItem == "function" ? s : Lt();
  return at({
    ...t,
    store: n
  });
};
function nt(t) {
  return typeof requestAnimationFrame == "function" ? requestAnimationFrame(t) : setTimeout(t, 0);
}
const kt = (t, e, s, n) => {
  const i = n?.duration ?? 300, r = n?.easing ?? ((y) => y), o = n?.equals ?? f;
  let l = n?.interpolate, a = t, c = e(), h = performance.now(), u = null, p = !0;
  const g = new V(e, o), d = C(t, o);
  d.onDispose(() => {
    u !== null && cancelAnimationFrame(u);
  }), d.onDispose(() => g.dispose()), s.forEach((y) => {
    y.setDerivative(g), y.onDispose(() => d.dispose());
  });
  const w = (y) => {
    c = y, h = performance.now(), a = d.value, p && (p = !1, u = nt(I));
  }, I = () => {
    const A = (performance.now() - h) / S.get(i), N = r(A);
    l == null && (l = Ct(a));
    let m = l(a, c, N);
    A >= 1 ? (p = !0, m = c) : u = nt(I), d.set(m);
  };
  return g.on(w), d;
}, qt = (t, e) => {
  const { initialValue: s, ...n } = e ?? {};
  return kt(
    /* c8 ignore next 2 */
    s ?? t.get(),
    () => t.get(),
    [t],
    n
  );
}, At = (t, e) => {
  const s = Object.values(t).filter(v.is), n = Object.keys(t);
  return U(() => {
    const i = {};
    for (const r of n)
      i[r] = S.get(t[r]);
    return e(i);
  }, s);
}, jt = (t) => At(t, (e) => e), Rt = (t, e) => {
  const s = C(t.get());
  let n = null;
  const i = t.on(
    (r) => {
      n != null && clearTimeout(n), n = setTimeout(
        () => {
          n = null, s.set(r);
        },
        typeof e == "function" ? e(r) : e
      );
    },
    { noAutoDispose: !0 }
  );
  return s.onDispose(() => {
    i(), n != null && clearTimeout(n);
  }), s;
}, Qt = (t) => {
  let e;
  return t.map((s) => {
    const n = e;
    return e = s, n;
  });
}, Ut = ({
  size: t = void 0,
  signal: e
}) => {
  const s = [];
  return e.map((n) => (s.push(n), t != null && s.length > t && s.shift(), s.slice()));
}, zt = (t) => (...e) => x(
  t,
  ...e
)((s, ...n) => s(...n));
function Jt(...t) {
  return x(...t)((...e) => {
    for (const s of e) if (s != null) return s;
  });
}
const Gt = (t, {
  channel: e,
  serialize: s = JSON.stringify,
  deserialize: n = JSON.parse,
  equals: i = f
}) => {
  const r = G();
  if (typeof r?.BroadcastChannel != "function")
    return () => {
    };
  const o = new r.BroadcastChannel(
    `tempo:syncProp:${e}`
  ), l = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  let a = !1;
  const c = (p) => {
    const g = p.data;
    if (!(g == null || typeof g != "object" || typeof g.value != "string" || g.sourceId != null && g.sourceId === l))
      try {
        a = !0;
        const d = n(g.value);
        i(t.get(), d) || t.set(d);
      } catch (d) {
        console.warn(
          `Failed to sync prop for channel "${e}" via BroadcastChannel`,
          d
        );
      } finally {
        a = !1;
      }
  };
  o.addEventListener("message", c);
  const h = t.on((p, g) => {
    if (!a && g !== void 0 && !i(p, g))
      try {
        const d = s(p);
        o.postMessage({
          value: d,
          sourceId: l
        });
      } catch (d) {
        console.warn(
          `Failed to serialize prop for channel "${e}" via BroadcastChannel`,
          d
        );
      }
  }), u = () => {
    h(), o.removeEventListener("message", c), o.close();
  };
  return t.onDispose(u), u;
};
function Wt(...t) {
  return x(...t)((...e) => e.every((s) => s));
}
function Ht(...t) {
  return x(...t)((...e) => e.some((s) => s));
}
function Xt(t) {
  return S.map(t, (e) => !e);
}
function Yt(t) {
  return S.map(t, (e) => e != null);
}
const Zt = (t, e) => {
  const s = C(t.get());
  let n = null, i = 0, r = null;
  const o = t.on(
    (l) => {
      const a = Date.now(), c = a - i;
      c >= e ? (i = a, s.set(l)) : (r = { value: l }, n == null && (n = setTimeout(() => {
        n = null, r != null && (i = Date.now(), s.set(r.value), r = null);
      }, e - c)));
    },
    { skipInitial: !0, noAutoDispose: !0 }
  );
  return s.onDispose(() => {
    o(), n != null && clearTimeout(n);
  }), s;
}, Kt = (t, e = f) => {
  const s = C(t.get(), e), n = t.on(
    (i) => {
      s.set(i);
    },
    { skipInitial: !0, noAutoDispose: !0 }
  );
  return s.onDispose(n), s;
}, te = (t, e, s, n = f) => {
  let i = e(s, t.get());
  const r = C(i, n), o = t.on(
    (l) => {
      i = e(i, l), r.set(i);
    },
    { skipInitial: !0, noAutoDispose: !0 }
  );
  return r.onDispose(o), r;
}, ee = (t, e = f) => {
  const s = /* @__PURE__ */ new Map();
  let n = t.get();
  return t.on(
    (i) => {
      const r = n;
      n = i;
      const o = s.get(r);
      if (o)
        for (const a of o) a.set(!1);
      const l = s.get(i);
      if (l)
        for (const a of l) a.set(!0);
    },
    { skipInitial: !0, noAutoDispose: !0 }
  ), (i) => {
    const r = C(e(i, n));
    Q()?.onDispose(() => r.dispose());
    let o = s.get(i);
    return o || (o = /* @__PURE__ */ new Set(), s.set(i, o)), o.add(r), r.onDispose(() => {
      const l = s.get(i);
      l && (l.delete(r), l.size === 0 && s.delete(i));
    }), r;
  };
};
function se(t, e, s) {
  let n, i;
  L.is(t) && (e === void 0 || typeof e == "object") ? (n = (_) => t.set(_), i = e) : (n = e, i = s);
  const r = i?.maxSize ?? 100, o = i?.filter, l = [t.value];
  let a = 0, c = !1, h = !1, u = !1, p = 0;
  const g = F(() => new L(!1, f)), d = F(() => new L(!1, f)), w = F(
    () => new L(l.slice(), f)
  ), I = F(() => new L(0, f)), y = () => {
    g.set(a > 0), d.set(a < l.length - 1), w.set(l.slice()), I.set(a);
  }, A = t.on(
    (_, P) => {
      if (!(c || h || u) && P !== void 0 && !(o != null && !o(_, P))) {
        if (l.length = a + 1, l.push(_), a = l.length - 1, r > 0 && l.length > r) {
          const T = l.length - r;
          l.splice(0, T), a -= T;
        }
        y();
      }
    },
    { skipInitial: !0, noAutoDispose: !0 }
  ), N = (_) => {
    _ < 0 || _ >= l.length || _ === a || (c = !0, a = _, n(l[a]), c = !1, y());
  }, m = () => {
    a <= 0 || N(a - 1);
  }, M = () => {
    a >= l.length - 1 || N(a + 1);
  }, E = (_) => {
    N(_);
  }, O = (_) => {
    n(_);
  }, k = (_) => {
    const P = _ !== void 0 ? _ : t.value;
    _ !== void 0 && (c = !0, n(P), c = !1), l.length = 0, l.push(P), a = 0, y();
  }, B = () => (h = !0, () => {
    h = !1;
  }), D = (_) => {
    u = !0, p = a;
    const P = l[a];
    _(), u = !1;
    const T = t.value;
    if (!t.equals(P, T)) {
      if (o != null && !o(T, P)) return;
      if (l.length = p + 1, l.push(T), a = l.length - 1, r > 0 && l.length > r) {
        const tt = l.length - r;
        l.splice(0, tt), a -= tt;
      }
      y();
    }
  }, $ = () => {
    A(), g.dispose(), d.dispose(), w.dispose(), I.dispose();
  }, K = Q();
  return K?.onDispose($), {
    signal: t,
    set: O,
    undo: m,
    redo: M,
    go: E,
    canUndo: g,
    canRedo: d,
    entries: w,
    index: I,
    clear: k,
    pause: B,
    transaction: D,
    dispose: $
  };
}
class ne {
  /**
   * Creates a new instance of `ElementPosition`.
   * @param index - The index of the element.
   * @param total - The total number of elements in the collection.
   */
  constructor(e, s) {
    this.index = e, this.total = s, this.counter = e + 1, this.isFirst = e === 0, this.isEven = e % 2 === 1, this.isOdd = e % 2 === 0;
  }
  /**
   * The counter of the element starting from 1.
   */
  counter;
  /**
   * Checks if the element is the first element in the collection.
   * @returns `true` if the element is the first element, `false` otherwise.
   */
  isFirst;
  /**
   * Checks if the counter of the element is even.
   * @returns `true` if the counter is even, `false` otherwise.
   */
  isEven;
  /**
   * Checks if the counter of the element is odd.
   * @returns `true` if the counter is odd, `false` otherwise.
   */
  isOdd;
  #t;
  /**
   * Checks if the element is the last element in the collection.
   * @returns `true` if the element is the last element, `false` otherwise.
   */
  get isLast() {
    return (this.#t == null || this.#t.isDisposed()) && (this.#t = this.total.map((e) => this.counter === e)), this.#t;
  }
  /**
   * Disposes the internal signal created by `isLast`.
   *
   * **Note:** With automatic signal disposal, this method is now a no-op when used within
   * a disposal scope (e.g., inside a renderable). The signal created by `isLast` is
   * automatically tracked and disposed when the scope ends. This method is kept for
   * backward compatibility and for cases where ElementPosition is used outside a scope.
   */
  dispose() {
    this.#t?.dispose(), this.#t = void 0;
  }
}
class ie {
  /**
   * Creates a new instance of `KeyedPosition`.
   * @param initialIndex - The initial index of the element.
   * @param total - A reactive signal representing the total number of elements in the collection.
   */
  constructor(e, s) {
    this.total = s, this.#o = e;
  }
  #t = null;
  #e;
  #s;
  #n;
  #i;
  #r;
  #o;
  /**
   * Updates the stored index. If the index signal has been created (because
   * user code accessed it), the signal is also updated reactively.
   * @internal
   */
  setIndex(e) {
    this.#o = e, this.#t !== null && this.#t.set(e);
  }
  /**
   * The reactive index of the element.
   * Created lazily on first access.
   */
  get index() {
    return this.#t === null && (this.#t = C(this.#o)), this.#t;
  }
  /**
   * The 1-based counter (index + 1).
   */
  get counter() {
    return this.#e == null && (this.#e = this.index.map((e) => e + 1)), this.#e;
  }
  /**
   * Whether this is the first element in the collection.
   */
  get isFirst() {
    return this.#s == null && (this.#s = this.index.map((e) => e === 0)), this.#s;
  }
  /**
   * Whether the counter is even.
   */
  get isEven() {
    return this.#n == null && (this.#n = this.index.map((e) => e % 2 === 1)), this.#n;
  }
  /**
   * Whether the counter is odd.
   */
  get isOdd() {
    return this.#i == null && (this.#i = this.index.map((e) => e % 2 === 0)), this.#i;
  }
  /**
   * Whether this is the last element in the collection.
   */
  get isLast() {
    return this.#r == null && (this.#r = U(
      () => this.index.value + 1 === this.total.value,
      [this.index, this.total]
    )), this.#r;
  }
  /**
   * Disposes the internal signals created by this position.
   *
   * **Note:** With automatic signal disposal, this method is typically a no-op
   * when used within a disposal scope (e.g., inside a renderable). Kept for
   * backward compatibility and edge cases.
   */
  dispose() {
    this.#t?.dispose(), this.#e?.dispose(), this.#s?.dispose(), this.#n?.dispose(), this.#i?.dispose(), this.#r?.dispose();
  }
}
const W = Math.PI, ct = W / 2, R = 1.70158, z = R * 1.525, J = 7.5625, q = 2.75, ut = 2 * W / 3, it = 2 * W / 4.5, re = (t) => t, oe = (t) => t * t, le = (t) => t * (2 - t), ae = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t, ce = (t) => t * t * t, ue = (t) => {
  const e = t - 1;
  return e * e * e + 1;
}, he = (t) => t < 0.5 ? 4 * t * t * t : 1 + (t - 1) * (2 * t - 2) * (2 * t - 2), fe = (t) => t * t * t * t, de = (t) => {
  const e = t - 1;
  return 1 - e * e * e * e;
}, pe = (t) => {
  if (t < 0.5) return 8 * t * t * t * t;
  const e = t - 1;
  return 1 - 8 * e * e * e * e;
}, ge = (t) => 1 - Math.cos(t * ct), _e = (t) => Math.sin(t * ct), me = (t) => -(Math.cos(W * t) - 1) / 2, ve = (t) => t === 0 ? 0 : Math.pow(2, 10 * t - 10), ye = (t) => t === 1 ? 1 : 1 - Math.pow(2, -10 * t), Se = (t) => t === 0 ? 0 : t === 1 ? 1 : t < 0.5 ? Math.pow(2, 20 * t - 10) / 2 : (2 - Math.pow(2, -20 * t + 10)) / 2, we = (t) => (R + 1) * t * t * t - R * t * t, De = (t) => {
  const e = t - 1;
  return 1 + (R + 1) * e * e * e + R * e * e;
}, Ie = (t) => {
  if (t < 0.5) {
    const s = 2 * t;
    return s * s * ((z + 1) * s - z) / 2;
  }
  const e = 2 * t - 2;
  return (e * e * ((z + 1) * e + z) + 2) / 2;
}, Z = (t) => {
  if (t < 1 / q) return J * t * t;
  if (t < 2 / q) {
    const s = t - 1.5 / q;
    return J * s * s + 0.75;
  }
  if (t < 2.5 / q) {
    const s = t - 2.25 / q;
    return J * s * s + 0.9375;
  }
  const e = t - 2.625 / q;
  return J * e * e + 0.984375;
}, be = (t) => 1 - Z(1 - t), Ce = (t) => t < 0.5 ? (1 - Z(1 - 2 * t)) / 2 : (1 + Z(2 * t - 1)) / 2, Ee = (t) => t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * ut) + 1, Le = (t) => t === 0 ? 0 : t === 1 ? 1 : -Math.pow(2, 10 * t - 10) * Math.sin((t * 10 - 10.75) * ut), ke = (t) => t === 0 ? 0 : t === 1 ? 1 : t < 0.5 ? -(Math.pow(2, 20 * t - 10) * Math.sin((20 * t - 11.125) * it)) / 2 : Math.pow(2, -20 * t + 10) * Math.sin((20 * t - 11.125) * it) / 2 + 1, Ae = (t) => (e) => 1 - t(1 - e), Me = (t) => (e) => e < 0.5 ? t(2 * e) / 2 : (2 - t(2 * (1 - e))) / 2, Oe = (t, e) => (s) => s < 0.5 ? t(2 * s) / 2 : 0.5 + e(2 * s - 1) / 2;
export {
  V as Computed,
  St as DisposalScope,
  ne as ElementPosition,
  ie as KeyedPosition,
  lt as MemoryStore,
  L as Prop,
  v as Signal,
  S as Value,
  te as accumulateSignal,
  Wt as and,
  qt as animateSignal,
  kt as animateSignals,
  zt as bind,
  Oe as chainEasing,
  Jt as coalesce,
  U as computed,
  x as computedOf,
  Nt as computedOfAsync,
  Ft as computedOfAsyncGenerator,
  At as computedRecord,
  Ot as createRenderable,
  ee as createSelector,
  Rt as delaySignal,
  Kt as distinctUntilChanged,
  we as easeInBack,
  be as easeInBounce,
  ce as easeInCubic,
  Le as easeInElastic,
  ve as easeInExpo,
  Ie as easeInOutBack,
  Ce as easeInOutBounce,
  he as easeInOutCubic,
  ke as easeInOutElastic,
  Se as easeInOutExpo,
  ae as easeInOutQuad,
  pe as easeInOutQuart,
  me as easeInOutSine,
  oe as easeInQuad,
  fe as easeInQuart,
  ge as easeInSine,
  De as easeOutBack,
  Z as easeOutBounce,
  ue as easeOutCubic,
  Ee as easeOutElastic,
  ye as easeOutExpo,
  le as easeOutQuad,
  de as easeOutQuart,
  _e as easeOutSine,
  rt as effect,
  yt as effectOf,
  bt as endInterpolate,
  Q as getCurrentScope,
  xt as getParentScope,
  Pt as getScopeStack,
  Ct as guessInterpolate,
  It as interpolateDate,
  wt as interpolateNumber,
  Dt as interpolateString,
  Vt as joinSignals,
  re as linear,
  $t as localStorageProp,
  Mt as makeProviderMark,
  jt as merge,
  Me as mirrorEasing,
  Xt as not,
  Yt as notNil,
  Ht as or,
  ft as popScope,
  Qt as previousSignal,
  C as prop,
  se as propHistory,
  ht as pushScope,
  Ae as reverseEasing,
  b as scopeStack,
  Bt as scoped,
  Tt as sessionStorageProp,
  gt as signal,
  Ut as slidingWindowSignal,
  at as storedProp,
  f as strictEquals,
  Gt as syncProp,
  Zt as throttleSignal,
  F as untracked,
  Y as withScope
};
